/**
 * Tool Registry - Auto-registers all available tools
 * 
 * Import this module to populate the ToolRegistry with all tools.
 */

import { ToolRegistry } from '@/engine/ToolRegistry';

// Converter Tools
import { BaseConverterTool, ColorConverterTool, CsvToJsonTool, CsvToMarkdownTableTool, CsvToSqlTool, CsvToXmlTool, CsvToYamlTool, DelimitedFormatConverterTool,HtmlToJsxTool, JsonToCsvTool, JsonToTomlTool, JsonToTsvTool, JsonToXmlTool, JsonToYamlTool, MarkdownToHtmlTool, TomlToJsonTool, TomlToYamlTool, TsvToJsonTool, XmlToCsvTool, XmlToJsonTool, XmlToYamlTool, YamlToCsvTool, YamlToJsonTool, YamlToTomlTool, YamlToXmlTool } from './converters';
// Encoder & Decoder Tools
import { Base64DecodeTool, Base64EncodeTool, Base64ImageConverterTool, BinaryToTextTool, HashGeneratorTool, HexDecodeTool, HexEncodeTool, HmacGeneratorTool, HtmlEntityDecodeTool, HtmlEntityEncodeTool, JwtDecodeTool,LzCompressTool, LzDecompressTool, MorseCodeTool, Rot13Tool, TextToBinaryTool, UrlDecodeTool, UrlEncodeTool } from './encoders';
// Formatter & Minifier Tools
import { CssFormatTool, CssMinifyTool, HtmlFormatTool, HtmlMinifyTool, JsFormatTool, JsMinifyTool, JsonFormatTool, JsonMinifyTool, SqlFormatTool, SqlMinifyTool, TomlFormatTool, TomlMinifyTool,XmlFormatTool, XmlMinifyTool, YamlFormatTool, YamlMinifyTool } from './formatters';
// Generator Tools
import { ColorPaletteGeneratorTool,GitignoreGeneratorTool, LoremIpsumTool, MetaTagGeneratorTool, NanoIdGeneratorTool, PasswordGeneratorTool, QrCodeDecoderTool, QrCodeGeneratorTool, RandomNumberGeneratorTool, RandomStringGeneratorTool, RandomYamlGeneratorTool, RobotsTxtGeneratorTool, SlugifyGeneratorTool, UuidGeneratorTool } from './generators';
// Text Tools
import { CaseConverterTool, CharacterCountTool, DeduplicateLinesTool, ExtractEmailsTool, ExtractUrlsTool,FindReplaceTool, LineCountTool, RemoveEmptyLinesTool, ReverseTextTool, SortLinesTool, WhitespaceRemoverTool, WordCountTool } from './text';
// Utility Tools
import { AndroidDimensScalerTool, AspectRatioCalculatorTool, CronExplainTool, DateParserTool, DpiCalculatorTool, FileSizeCalculatorTool, PercentageCalculatorTool, TimeZoneConverterTool, UnixTimestampTool, UrlParserTool, UuidAnalyzerTool } from './utilities';
// Validator & Tester Tools
import { CssValidateTool, EmailValidateTool, JsonValidateTool, ManifestPermissionCheckerTool, PasswordStrengthCheckerTool, RegexTesterTool, RegexValidateTool, TomlValidateTool,UrlValidateTool, XmlValidateTool, YamlValidateTool } from './validators';
// Visualizer Tools
// Visualizer Tools — registered in ToolRegistry so they appear in the browser,
// search, and command palette. When selected, the UI applies them as panel
// display modes instead of pipeline steps (see visualizerToolMapping.ts).
import { DiffViewerTool, EditorVisualizerTool,HexVisualizerTool, JsonDiagramVisualizerTool, JsonTreeVisualizerTool, MarkdownPreviewVisualizerTool, RegexRailroadVisualizerTool, StructuredTableVisualizerTool, TabularDataVisualizerTool } from './visualizers';

/**
 * Register all tools with the ToolRegistry singleton.
 */
export function registerAllTools(): void {
  // Converter Tools
  ToolRegistry.register(ColorConverterTool);
  ToolRegistry.register(MarkdownToHtmlTool);
  ToolRegistry.register(BaseConverterTool);
  ToolRegistry.register(CsvToJsonTool);
  ToolRegistry.register(CsvToMarkdownTableTool);
  ToolRegistry.register(CsvToXmlTool);
  ToolRegistry.register(CsvToYamlTool);
  ToolRegistry.register(JsonToCsvTool);
  ToolRegistry.register(JsonToTsvTool);
  ToolRegistry.register(TsvToJsonTool);
  ToolRegistry.register(JsonToXmlTool);
  ToolRegistry.register(XmlToCsvTool);
  ToolRegistry.register(XmlToJsonTool);
  ToolRegistry.register(XmlToYamlTool);
  ToolRegistry.register(JsonToYamlTool);
  ToolRegistry.register(YamlToCsvTool);
  ToolRegistry.register(YamlToJsonTool);
  ToolRegistry.register(YamlToXmlTool);
  ToolRegistry.register(TomlToJsonTool);
  ToolRegistry.register(TomlToYamlTool);
  ToolRegistry.register(JsonToTomlTool);
  ToolRegistry.register(YamlToTomlTool);
  ToolRegistry.register(HtmlToJsxTool);
  ToolRegistry.register(CsvToSqlTool);
  ToolRegistry.register(DelimitedFormatConverterTool);

  // Encoder & Decoder Tools
  ToolRegistry.register(Base64EncodeTool);
  ToolRegistry.register(Base64DecodeTool);
  ToolRegistry.register(UrlEncodeTool);
  ToolRegistry.register(UrlDecodeTool);
  ToolRegistry.register(HexEncodeTool);
  ToolRegistry.register(HexDecodeTool);
  ToolRegistry.register(Rot13Tool);
  ToolRegistry.register(MorseCodeTool);
  ToolRegistry.register(LzCompressTool);
  ToolRegistry.register(LzDecompressTool);
  ToolRegistry.register(TextToBinaryTool);
  ToolRegistry.register(BinaryToTextTool);
  ToolRegistry.register(HashGeneratorTool);
  ToolRegistry.register(HmacGeneratorTool);
  ToolRegistry.register(HtmlEntityEncodeTool);
  ToolRegistry.register(HtmlEntityDecodeTool);
  ToolRegistry.register(Base64ImageConverterTool);
  ToolRegistry.register(JwtDecodeTool);

  // Formatter & Minifier Tools
  ToolRegistry.register(CssFormatTool);
  ToolRegistry.register(CssMinifyTool);
  ToolRegistry.register(HtmlFormatTool);
  ToolRegistry.register(HtmlMinifyTool);
  ToolRegistry.register(JsFormatTool);
  ToolRegistry.register(JsMinifyTool);
  ToolRegistry.register(JsonFormatTool);
  ToolRegistry.register(JsonMinifyTool);
  ToolRegistry.register(SqlFormatTool);
  ToolRegistry.register(SqlMinifyTool);
  ToolRegistry.register(XmlFormatTool);
  ToolRegistry.register(XmlMinifyTool);
  ToolRegistry.register(YamlFormatTool);
  ToolRegistry.register(YamlMinifyTool);
  ToolRegistry.register(TomlFormatTool);
  ToolRegistry.register(TomlMinifyTool);

  // Generator Tools
  ToolRegistry.register(UuidGeneratorTool);
  ToolRegistry.register(LoremIpsumTool);
  ToolRegistry.register(NanoIdGeneratorTool);
  ToolRegistry.register(SlugifyGeneratorTool);
  ToolRegistry.register(RandomStringGeneratorTool);
  ToolRegistry.register(RandomNumberGeneratorTool);
  ToolRegistry.register(MetaTagGeneratorTool);
  ToolRegistry.register(QrCodeGeneratorTool);
  ToolRegistry.register(QrCodeDecoderTool);
  ToolRegistry.register(PasswordGeneratorTool);
  ToolRegistry.register(RandomYamlGeneratorTool);
  ToolRegistry.register(GitignoreGeneratorTool);
  ToolRegistry.register(RobotsTxtGeneratorTool);
  ToolRegistry.register(ColorPaletteGeneratorTool);

  // Text Tools
  ToolRegistry.register(FindReplaceTool);
  ToolRegistry.register(SortLinesTool);
  ToolRegistry.register(CaseConverterTool);
  ToolRegistry.register(WordCountTool);
  ToolRegistry.register(CharacterCountTool);
  ToolRegistry.register(LineCountTool);
  ToolRegistry.register(DeduplicateLinesTool);
  ToolRegistry.register(RemoveEmptyLinesTool);
  ToolRegistry.register(ReverseTextTool);
  ToolRegistry.register(WhitespaceRemoverTool);
  ToolRegistry.register(ExtractEmailsTool);
  ToolRegistry.register(ExtractUrlsTool);

  // Utility Tools
  ToolRegistry.register(AndroidDimensScalerTool);
  ToolRegistry.register(AspectRatioCalculatorTool);
  ToolRegistry.register(DpiCalculatorTool);
  ToolRegistry.register(FileSizeCalculatorTool);
  ToolRegistry.register(PercentageCalculatorTool);
  ToolRegistry.register(CronExplainTool);
  ToolRegistry.register(DateParserTool);
  ToolRegistry.register(TimeZoneConverterTool);
  ToolRegistry.register(UnixTimestampTool);
  ToolRegistry.register(UrlParserTool);
  ToolRegistry.register(UuidAnalyzerTool);

  // Validator & Tester Tools
  ToolRegistry.register(ManifestPermissionCheckerTool);
  ToolRegistry.register(JsonValidateTool);
  ToolRegistry.register(RegexTesterTool);
  ToolRegistry.register(PasswordStrengthCheckerTool);
  ToolRegistry.register(CssValidateTool);
  ToolRegistry.register(EmailValidateTool);
  ToolRegistry.register(RegexValidateTool);
  ToolRegistry.register(UrlValidateTool);
  ToolRegistry.register(XmlValidateTool);
  ToolRegistry.register(YamlValidateTool);
  ToolRegistry.register(TomlValidateTool);

  // Visualizer Tools — appear in browser/search but apply as panel modes, not pipeline steps
  ToolRegistry.register(EditorVisualizerTool);
  ToolRegistry.register(JsonTreeVisualizerTool);
  ToolRegistry.register(DiffViewerTool);
  ToolRegistry.register(TabularDataVisualizerTool);
  ToolRegistry.register(StructuredTableVisualizerTool);
  ToolRegistry.register(JsonDiagramVisualizerTool);
  ToolRegistry.register(MarkdownPreviewVisualizerTool);
  ToolRegistry.register(RegexRailroadVisualizerTool);
  ToolRegistry.register(HexVisualizerTool);
}

// Re-export all tools for convenience
export * from './converters';
export * from './encoders';
export * from './formatters';
export * from './generators';
export * from './text';
export * from './utilities';
export * from './validators';
export * from './visualizers';
